import { createContext, useContext, useState, useEffect } from "react";
import { StringUtils } from "../../../../../core/utils/string_utils";
import { userRepository, trackingService } from "../../../di/di";
import { useNavigate } from "react-router-dom";
import { DashboardRoute } from "../../../../../core/routes/route_constants";
import { toast } from "sonner";
const SignUpContext = createContext();

export const SignUpContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [name, setName] = useState("");
  const [nameErrorMessage, setNameErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] =
    useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [otpCode, setOtpCode] = useState(["", "", "", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    trackingService.trackEvent("signup_page_viewed", { timestamp: new Date().toISOString() });
  }, []);

  const isFormDisabled =
    nameErrorMessage ||
    emailErrorMessage ||
    passwordErrorMessage ||
    isProcessing;

  const handleNameChange = (e) => {
    setNameErrorMessage("");
    const value = e.target.value;
    setName(value);

    if (!value) {
      setNameErrorMessage("Name is required");
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmailErrorMessage("");
    setEmail(value);

    const validation = StringUtils.isEmailValid(value);

    if (!validation.isValid) {
      setEmailErrorMessage(validation.message);
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPasswordErrorMessage("");
    setPassword(value);
    setConfirmPassword("");

    const validation = StringUtils.isPasswordValid(value);

    if (!validation.isValid) {
      setPasswordErrorMessage(validation.message);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPasswordErrorMessage("");
    setConfirmPassword(value);

    const validation = StringUtils.isPasswordValid(value);

    if (!validation.isValid) {
      setConfirmPasswordErrorMessage(validation.message);
    } else if (password !== value) {
      setConfirmPasswordErrorMessage("Passwords do not match");
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (nameErrorMessage || emailErrorMessage || passwordErrorMessage || confirmPasswordErrorMessage) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");

    const capitalizedName = StringUtils.capitalizeWords(name);
    const response = await userRepository.register(capitalizedName, email, password);
    setIsProcessing(false);

    if (!response.success) {
      setErrorMessage(response.message);
      return;
    }

    setShowOtpVerification(true);
  };

  const handleOtpChange = (index, value) => {
    // Update the OTP code at the specified index
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    setErrorMessage("");

    // Auto-focus next input if value is entered
    if (value !== "" && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    } else if (value === "" && index > 0) {
      const nextInput = document.getElementById(`otp-${index - 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleVerifyOtp = async () => {
    setVerifying(true);
    setErrorMessage("");

    const otp = otpCode.join("");
    const response = await userRepository.validateOtp(email, otp);

    if (!response.success) {
      setErrorMessage(response.message);
      setVerifying(false);
      return;
    }
    const loginResponse = await userRepository.login(email, password);
    if (!loginResponse.success) {
      setErrorMessage(loginResponse.message);
      return;
    }
    setShowOtpVerification(false);
    setVerifying(false);
    trackingService.identify(loginResponse.data?.userId, {
      distinct_id: loginResponse.data?.userId,
      timestamp: new Date().toISOString()
    });
    navigate(DashboardRoute);
  };

  const handleLogin = async () => {
    setErrorMessage("");
    setIsProcessing(true);

    const response = await userRepository.login(email, password);
    setIsProcessing(false);

    if (!response.success) {
      setErrorMessage(response.message);
      return;
    }

    navigate(DashboardRoute);
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    const response = await userRepository.resendOtp(email);
    if (!response.success) {
      setErrorMessage(response.message);
      return;
    }

    setResendTimer(60); // Set 60 seconds timer
    toast.success('OTP sent successfully');
  };

  return (
    <SignUpContext.Provider
      value={{
        //states
        errorMessage,
        name,
        nameErrorMessage,
        email,
        emailErrorMessage,
        password,
        passwordErrorMessage,
        confirmPassword,
        confirmPasswordErrorMessage,
        isProcessing,
        showPassword,
        showConfirmPassword,
        isFormDisabled,
        isSuccess,
        showOtpVerification,
        otpCode,
        verifying,
        resendTimer,

        //setters
        setErrorMessage,
        setName,
        setNameErrorMessage,
        setEmail,
        setEmailErrorMessage,
        setPassword,
        setPasswordErrorMessage,
        setShowPassword,
        setConfirmPassword,
        setConfirmPasswordErrorMessage,
        setShowConfirmPassword,
        setOtpCode,

        //handlers
        handleNameChange,
        handleEmailChange,
        handlePasswordChange,
        handleConfirmPasswordChange,
        handleSignUp,
        handleLogin,
        handleOtpChange,
        handleVerifyOtp,
        handleResendOtp
      }}
    >
      {children}
    </SignUpContext.Provider>
  );
};

export const useSignUpContext = () => {
  return useContext(SignUpContext);
};
